import { Button, makeStyles, tokens } from '@fluentui/react-components';
import { useT } from '@powerportalspro/react';
import { useId, useState } from 'react';
import { MultiTableSearch } from '@powerportalspro/react-fluent';
import { ACCOUNT_ID } from './DetailPageRecords';
import { DemoShowcaseHeading, DemoShowcaseLayout } from './DemoShowcaseLayout';

// The search as a record's page would have it: only the rows about Schmeler and Sons, through each
// table's regarding column — its contacts, its opportunities, any account under it. Above it, a few
// searches that find something here, each a click away. The search is the page's own, controlled and
// kept out of the address, so a click is all it takes to change it.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.MultiTableSearchShowcase.MultiTableSearchRecordMainDemo';

const ACCOUNT = { tableName: 'account', id: ACCOUNT_ID };

// Searches that find several things about Schmeler and Sons: its contacts and open deals by its name,
// its contacts by their email, and its notes. The tips say what each shows.
const SUGGESTIONS = ['schmeler', '*gmail', 'delivery'];

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
  },
  suggestions: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  list: {
    margin: 0,
    paddingInlineStart: tokens.spacingHorizontalXL,
  },
});

// The strings are HTML, written once for both stacks.
function Item({ text }: { text: string }) {
  return <li dangerouslySetInnerHTML={{ __html: text }} />;
}

export function MultiTableSearchRecordMainSample() {
  const styles = useStyles();
  const t = useT();
  const [search, setSearch] = useState<string>();
  const suggestionsId = useId();

  return (
    <DemoShowcaseLayout
      title={t(`${STRINGS_BASE}.try-title`)}
      tips={
        <>
          <ul className={styles.list}>
            <Item text={t(`${STRINGS_BASE}.try-suggestions`)} />
            <Item text={t(`${STRINGS_BASE}.try-company`)} />
            <Item text={t(`${STRINGS_BASE}.try-contains`)} />
            <Item text={t(`${STRINGS_BASE}.try-notes`)} />
            <Item text={t(`${STRINGS_BASE}.try-name`)} />
            <Item text={t(`${STRINGS_BASE}.try-grouped`)} />
          </ul>

          <DemoShowcaseHeading>{t(`${STRINGS_BASE}.rules-title`)}</DemoShowcaseHeading>
          <ul className={styles.list}>
            <Item text={t(`${STRINGS_BASE}.rule-regarding`)} />
            <Item text={t(`${STRINGS_BASE}.rule-unlinked`)} />
          </ul>
        </>
      }
    >
      <div className={styles.root}>
        <div className={styles.suggestions} role="group" aria-labelledby={suggestionsId}>
          <span id={suggestionsId}>{t(`${STRINGS_BASE}.suggestions-label`)}</span>
          {SUGGESTIONS.map((term) => (
            <Button key={term} size="small" appearance="outline" onClick={() => setSearch(term)}>
              {term}
            </Button>
          ))}
        </div>
        <MultiTableSearch
          regarding={ACCOUNT}
          search={search}
          onSearchChange={setSearch}
          searchParameterName={null}
          pageSize={10}
          groupByTable
        />
      </div>
    </DemoShowcaseLayout>
  );
}
